// Maps the raw GET /people/employees/{gpid} response onto the Employee Profile UI types.
// Defensive field access throughout — backend arrays (metrics12Week, coachingItems,
// recentActivities) may be empty or carry slightly different casing than the UI shape.
import { MyTeamMember, TeamMemberRosterStatus } from '../types';
import { WeeklyMetricRow } from '../BeginActivity/common/types/beginActivity.common.types';
import { EmployeeProfileDetails, EmployeeCoachingHistoryRow, EmployeeAllHistoryRow } from './types';
import { formatLongDate } from '../formatDate';
import { formatUserName } from '../../../utils/emailTouserName';
import type { CoachingHistoryItem, CoachingItemCategory, FollowUpResult } from '../BeginActivity/common/types/beginActivity.common.types';

const KNOWN_STATUSES: TeamMemberRosterStatus[] = ['Active', 'In Onboarding', 'On Leave', 'Inactive', 'Terminated'];

export interface EmployeeProfileApiResponse {
  employee: Record<string, any>;
  metrics12Week?: any[];
  metrics12Period?: any[];
  recentActivities?: any[];
  coachingItems?: any[];
  metricsContext?: Record<string, any>;
  currentPeriod?: Record<string, any>;
  onboardingActivities?: any[];
}

// Overrides the roster row's stale fields (status/role/manager/hireDate/etc.) with the
// freshly-fetched employee record so the header card always reflects live data.
export function mapEmployeeToMember(base: MyTeamMember, employee: Record<string, any>): MyTeamMember {
  if (!employee) return base;
  const name = [employee.firstName, employee.lastName].filter(Boolean).join(' ').trim();
  return {
    ...base,
    id: String(employee.employeeId ?? base.id),
    name: name || base.name,
    gpid: String(employee.gpid ?? base.gpid),
    manager: employee.manager ?? base.manager,
    role: employee.role ?? base.role,
    hireDate: employee.hireDate ?? base.hireDate,
    seniority: employee.tenure ?? base.seniority,
    status: KNOWN_STATUSES.includes(employee.status) ? employee.status : base.status,
  };
}

export function mapEmployeeProfileDetails(
  response: EmployeeProfileApiResponse,
  fallback: EmployeeProfileDetails,
): EmployeeProfileDetails {
  const employee = response.employee ?? {};
  const period = response.currentPeriod ?? {};
  const context = response.metricsContext ?? {};
  const currentPeriod =
    period.periodNum != null && period.fiscalWeek != null
      ? `P${period.periodNum} W${period.fiscalWeek}`
      : fallback.currentPeriod;

  return {
    manager: employee.manager ?? fallback.manager,
    startDate: formatLongDate(employee.hireDate) || fallback.startDate,
    level: fallback.level,
    service: employee.tenure ?? fallback.service,
    organization: fallback.organization,
    currentPeriod,
    location: employee.location ?? context.location ?? fallback.location,
    shift: fallback.shift,
  };
}

// metrics12Week / metrics12Period entries come back as flat per-week/per-period records
// (dps/ku/gapTime/casesPicked/sia) rather than pre-shaped WeeklyMetricRow objects, so we
// pivot them into one row per metric here. Targets mirror the My Team roster table.
interface MetricDef {
  key: 'dps' | 'ku' | 'gapTime' | 'casesPicked';
  metricName: string;
  unit: string;
  target: number | null;
  higherIsBetter: boolean;
}
const METRIC_DEFS: MetricDef[] = [
  { key: 'dps',        metricName: 'DPS',          unit: '%',     target: 1.0,  higherIsBetter: true },
  { key: 'ku',         metricName: 'KU',           unit: '%',     target: 0.7,  higherIsBetter: true },
  { key: 'gapTime',    metricName: 'Gap Time',     unit: 'sec',   target: 400,  higherIsBetter: false },
  { key: 'casesPicked', metricName: 'Cases Picked', unit: 'cases', target: 1150, higherIsBetter: true },
];

function pivotMetrics(entries: any[] | undefined, labelOf: (entry: any) => string): WeeklyMetricRow[] {
  if (!Array.isArray(entries) || entries.length === 0) return [];
  // API returns newest-first; display oldest → newest left to right.
  const ordered = [...entries].reverse();
  return METRIC_DEFS.map((def) => {
    const cells = ordered.map((entry) => {
      const raw = entry?.[def.key];
      const actual = typeof raw === 'number' ? raw : null;
      const target = def.target;
      const met = actual !== null && target !== null
        ? (def.higherIsBetter ? actual >= target : actual <= target)
        : null;
      return {
        weekLabel: labelOf(entry),
        actual,
        target,
        state: actual === null ? 'pending' : met ? 'met' : 'miss',
      } as const;
    });
    const valued = cells.filter((c) => c.actual !== null);
    const rollingAverage = valued.length
      ? valued.reduce((s, c) => s + (c.actual as number), 0) / valued.length
      : 0;
    return {
      metricName: def.metricName,
      unit: def.unit,
      rollingAverage,
      weeksMet: cells.filter((c) => c.state === 'met').length,
      weeksTotal: cells.length,
      cells: cells as any,
    };
  });
}

export function mapWeeklyMetricRows(entries: any[] | undefined): WeeklyMetricRow[] {
  return pivotMetrics(entries, (entry) => `W${entry.week}`);
}

export function mapPeriodMetricRows(entries: any[] | undefined): WeeklyMetricRow[] {
  return pivotMetrics(entries, (entry) => `P${entry.periodNum}`);
}

// Coaching item titles are built client-side as "${Category}: ${Focus} for ${EmployeeName}"
// (see buildNewActionPlanPayload) — the API doesn't send focus/category as separate fields,
// so this is the only way to recover the Focus Area for display.
function parseCoachingItemTitle(title: string | undefined): { category?: string; focus?: string } {
  if (!title) return {};
  const match = title.match(/^(.+?):\s*(.+?)\s+for\s+.+$/i);
  if (!match) return {};
  return { category: match[1].trim(), focus: match[2].trim() };
}

function isCoachingItemClosed(status: string | undefined): boolean {
  const normalized = (status ?? '').toLowerCase();
  return normalized.includes('complet') || normalized.includes('clos');
}

export function mapCoachingItems(
  items: any[] | undefined,
  employeeName: string,
): EmployeeCoachingHistoryRow[] {
  if (!Array.isArray(items)) return [];
  return items.map((item, idx) => {
    const { focus: parsedFocus } = parseCoachingItemTitle(item.title);
    return {
      id: String(item.actionPlanId ?? item.id ?? `coach-${idx}`),
      employeeName: item.employeeName ?? employeeName,
      actionType: item.actionType === 'Process' ? 'Process' : 'Metric',
      focusArea: item.focusArea ?? item.focus ?? parsedFocus ?? '—',
      actionPlan: item.actionPlan ?? item.plan ?? '—',
      managerSubmitted: item.managerSubmitted ?? item.submittedBy ?? '—',
      activityDate: item.activityDate ?? item.date ?? '—',
      // API doesn't send a source field yet — falls back to unknown rather than guessing an activity type.
      source: item.source ?? '—',
      status: isCoachingItemClosed(item.status) ? 'Closed' : 'Open',
      result: item.result ?? item.followUpResult ?? '—',
    };
  });
}

// Timestamps arrive as "2026-10-13T19:18:11.142533" — only the date part is displayed.
function formatActivityDate(value: string | null | undefined): string | null {
  if (!value) return null;
  return formatLongDate(value.slice(0, 10));
}

// Period arrives as "2026P11W42" — shown as "P11 W42" to match the header's period label.
function formatActivityPeriod(value: string | null | undefined): string {
  if (!value) return '—';
  const match = value.match(/P(\d+)W(\d+)/i);
  return match ? `P${match[1]} W${match[2]}` : value;
}

// API statuses are upper snake-case (NEW, IN_PROGRESS, COMPLETED, ...). Anything not yet
// finished is a Draft (shows Resume); only COMPLETED rows are shown as Completed.
function mapActivityStatus(status: string | undefined): EmployeeAllHistoryRow['status'] {
  const normalized = (status ?? '').toUpperCase().replace(/\s+/g, '_');
  if (normalized === 'MISSED') return 'Missed';
  if (normalized === 'COMPLETED' || normalized === 'COMPLETE') return 'Completed';
  return 'In Progress';
}

export function mapRecentActivities(
  items: any[] | undefined,
  employeeName: string,
  role: string,
): EmployeeAllHistoryRow[] {
  if (!Array.isArray(items)) return [];
  return items.map((item, idx) => {
    const createdBy = item.createdBy ?? item.submittedBy;
    return {
      id: String(item.id ?? `hist-${idx}`),
      employeeName: item.employeeName ?? employeeName,
      role: item.role ?? role,
      type: String(item.type ?? item.activityType ?? 'Routine Observation').replace(/_/g, ' ') as EmployeeAllHistoryRow['type'],
      result: item.result ?? item.status ?? '—',
      createdOn: formatActivityDate(item.createdAt) ?? item.createdOn ?? item.date ?? '—',
      createdBy: createdBy ? formatUserName(createdBy) : '—',
      finalized: formatActivityDate(item.completedAt) ?? item.finalized ?? item.completedOn ?? null,
      periodWeek: formatActivityPeriod(item.period) !== '—' ? formatActivityPeriod(item.period) : (item.periodWeek ?? '—'),
      status: mapActivityStatus(item.status),
      activityId: String(item.activityId ?? item.id ?? idx),
      rawActivityType: item.activityType,
      observationType: item.observationType,
      rawStatus: item.status,
    };
  });
}

// Splits the raw coachingItems array into the open/history buckets the activity screens
// (Pulse Check, Routine/Full Observation) render in their Coaching History section.
export function mapCoachingHistoryItems(
  items: any[] | undefined,
  employeeId: string,
): { openItems: CoachingHistoryItem[]; historyItems: CoachingHistoryItem[] } {
  if (!Array.isArray(items) || items.length === 0) {
    return { openItems: [], historyItems: [] };
  }
  const mapped: CoachingHistoryItem[] = items.map((item, idx) => {
    const isClosed = isCoachingItemClosed(item.status);
    const category: CoachingItemCategory = item.actionType === 'Process' ? 'Process' : 'Metric';
    // Closed items carry their actual recorded outcome in `result` — fall back to 'Complete'
    // only when the backend omits it, rather than always overwriting it.
    const rawResult = item.result ?? item.followUpResult;
    const followUpResult: FollowUpResult = isClosed
      ? ((rawResult as FollowUpResult) || 'Complete')
      : '';
    const { focus: parsedFocus } = parseCoachingItemTitle(item.title);
    return {
      id: String(item.actionPlanId ?? item.id ?? `coach-${idx}`),
      employeeId,
      // API doesn't send a source field yet — falls back to unknown rather than guessing an activity type.
      source: item.source ?? '—',
      activityId: String(item.activityId ?? item.actionPlanId ?? item.id ?? idx),
      date: item.activityDate ?? item.date ?? '',
      submittedBy: item.managerSubmitted ?? item.submittedBy ?? '—',
      category,
      focus: item.focusArea ?? item.focus ?? parsedFocus ?? '—',
      plan: item.actionPlan ?? item.plan ?? '—',
      status: isClosed ? 'Completed' : 'Pending',
      followUpResult,
      followUpFeedback: item.supervisorFeedback ?? '',
      completedOn: isClosed ? (item.completedOn ?? item.activityDate ?? item.date) : undefined,
      reviewHistory: [],
    };
  });
  return {
    openItems: mapped.filter((i) => i.status === 'Pending'),
    historyItems: mapped.filter((i) => i.status === 'Completed'),
  };
}


export function mapEmployeeOnboardingPlan(
  response: EmployeeProfileApiResponse | null | undefined,
  fallbackPlan: any,
): any {
  const onboarding = response?.onboardingActivities ?? response?.onboardingPlan;
  if (!onboarding) {
    return fallbackPlan;
  }

  const summary = onboarding.planSummary ?? {};
  const rawCheckpoints = onboarding.checkpoints ?? summary.checkpoints ?? [];
  const rawItems = onboarding.items ?? [];

  const phases = rawCheckpoints.map((cp: any) => ({
    key: cp.phase,
    short: cp.short,
    completed: cp.done ?? 0,
    total: cp.total ?? 0,
    state: cp.state ?? "ahead",
    current: cp.state === "current",
    overdue: cp.state === "late",
  }));

  const rows = rawItems.map((item: any, idx: number) => ({
    id: item.itemKey ?? item.id ?? `onboard-item-${idx}`,
    itemKey: item.itemKey,
    phase: item.phase ?? "Week 1",
    activity: item.title ?? item.text ?? "Activity",
    type: item.type ?? "Training",
    priority: item.priority ?? "Mandatory",
    due: item.due ?? item.dueDate ?? "—",
    dueDate: item.due ?? item.dueDate,
    status: item.status ?? (item.completed ? "Done" : "Open"),
    isImported: item.isImported ?? (item.status === "Done" && !item.completionRecord),
    completionRecord: Boolean(item.completionRecord),
    completedOn: item.completedOn,
    completedBy: item.completedBy,
    completionType: item.completionType,
    taskId: item.taskId,
    isOverdue: Boolean(item.isOverdue),
  }));

  return {
    dayLabel: summary.dayLabel ?? "Day 1 of 84",
    status: summary.status ?? "Active",
    completedCount: summary.completedItems ?? 0,
    totalCount: summary.totalItems ?? rows.length,
    overdueCount: summary.overdueItems ?? 0,
    openCount: summary.openItems ?? (rows.length - (summary.completedItems ?? 0)),
    phases,
    rows,
  };
}
